import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "../data/navLinks";
import { StaticImage } from "gatsby-plugin-image";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Brand Colors constants
  const BRAND_RED = "text-red-600";
  const BRAND_BG_BLACK = "bg-black"; // For context, though header is white

  // Handle Scroll Logic
  useEffect(() => {
    const handleScroll = () => {
      // If user scrolls down more than 50px, toggle state
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Container Wrapper: 
        1. Fixed position.
        2. Transition logic for width, top position, and border radius.
      */}
      <header
        className={`fixed z-50 left-0 right-0 transition-all duration-500 ease-in-out flex justify-center
          ${isScrolled ? "top-0 w-full" : "top-6 w-[95%] max-w-7xl mx-auto"}`}
      >
        <nav
          className={`relative w-full bg-white shadow-lg transition-all duration-500 ease-in-out flex items-center justify-between px-6
            ${
              isScrolled
                ? "rounded-none py-3" // Small height when stuck
                : "rounded-full py-5" // Taller height when floating
            }
          `}
        >
          {/* LOGO SECTION */}
          <div className="flex items-center gap-2 max-w-[200px] font-bold text-2xl tracking-tighter text-black cursor-pointer">
            <StaticImage
              src="../images/logo-ash.png"
              alt="Brand Focus logo"
              className="h-12 w-fit"
              objectFit="contain"
              placeholder="none"
              backgroundColor="#ffffff"
            />
          </div>

          {/* DESKTOP NAVIGATION */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link, index) => (
              <div
                key={index}
                className="relative group"
                onMouseEnter={() => link.hasDropdown && setActiveDropdown(index)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.href}
                  className={`flex items-center gap-1 font-medium transition-colors hover:text-red-600
                    ${activeDropdown === index ? "text-red-600" : "text-gray-800"}
                  `}
                >
                  {link.label}
                  {link.hasDropdown && <ChevronDown size={16} />}
                </Link>

                {/* MEGA MENU DROPDOWN */}
                {link.hasDropdown && activeDropdown === index && (
                  <div className="absolute top-full -left-20 pt-6 w-[600px]">
                    {/* The white box itself */}
                    <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden p-6 grid grid-cols-12 gap-6">
                      {/* Left Column: Description */}
                      <div className="col-span-4 bg-gray-50 rounded-lg p-4 flex flex-col justify-between">
                        <div>
                          <h4 className="font-bold text-lg text-black mb-2">{link.label}</h4>
                          <p className="text-sm text-gray-500 leading-relaxed">{link.description}</p>
                        </div>
                        <Link
                          to={link.href}
                          className="text-xs font-bold text-red-600 flex items-center gap-1 mt-4 hover:underline"
                        >
                          View Overview <ArrowRight size={12} />
                        </Link>
                      </div>

                      {/* Right Column: Sub-links Grid */}
                      <div className="col-span-8 grid grid-cols-2 gap-4 content-start">
                        {link.subLinks.map((sub, subIndex) => (
                          <Link
                            key={subIndex}
                            to={sub.href}
                            className="group/item block p-3 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <div className="text-sm font-semibold text-gray-900 group-hover/item:text-red-600">
                              {sub.name}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ACTION BUTTON & HAMBURGER */}
          <div className="flex items-center gap-4">
            <Link to="/contact" className="hidden sm:block">
              <button
                className="px-6 py-2 rounded-full font-semibold transition-all text-white bg-black hover:bg-red-600"
              >
                Get Started
              </button>
            </Link>

            {/* Mobile Toggle */}
            <button className="lg:hidden text-black" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </nav>

        {/* MOBILE MENU DRAWER */}
        <div
          className={`absolute top-full left-0 right-0 bg-white shadow-xl mt-2 rounded-2xl overflow-hidden transition-all duration-300 lg:hidden
            ${isMobileMenuOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible"}
          `}
        >
          <div className="flex flex-col p-6 max-h-[80vh] overflow-y-auto">
            {NAV_LINKS.map((link, index) => (
              <div key={index} className="border-b border-gray-100 last:border-0 py-4">
                <Link 
                  to={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex justify-between items-center text-lg font-semibold text-gray-900 hover:text-red-600 transition-colors"
                >
                  {link.label}
                  {link.hasDropdown && <ChevronDown size={16} className="text-gray-400" />}
                </Link>

                {/* Mobile Dropdown Content */}
                {link.hasDropdown && (
                  <div className="mt-4 pl-4 space-y-3 border-l-2 border-red-100 ml-1">
                    <p className="text-xs text-gray-400 mb-2 uppercase tracking-widest">
                      {link.description.substring(0, 30)}...
                    </p>
                    {link.subLinks.map((sub, subIdx) => (
                      <Link 
                        key={subIdx} 
                        to={sub.href} 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block text-gray-600 hover:text-red-600 transition-colors"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="w-full mt-6">
              <button className="w-full py-4 rounded-xl font-bold text-white bg-red-600 hover:bg-red-700 transition-colors">
                Get Started
              </button>
            </Link>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
